// Shared client for the trello-board Netlify function (read-only proxy;
// Trello credentials stay server-side). Used by the client detail Trello
// panel and the Client Comms board explorer.

import { supabase } from './supabase';

export interface TrelloLabel {
  id: string;
  name: string;
  color: string | null;
}

export interface TrelloList {
  id: string;
  name: string;
  pos: number;
}

export interface TrelloCard {
  id: string;
  name: string;
  idList: string;
  pos: number;
  due: string | null;
  dueComplete: boolean;
  shortUrl: string;
  labels: TrelloLabel[];
  badges?: { comments?: number; attachments?: number };
}

export interface TrelloCardDetail {
  id: string;
  name: string;
  desc: string;
  due: string | null;
  dueComplete: boolean;
  dateLastActivity: string;
  shortUrl: string;
  labels: TrelloLabel[];
  list?: { id: string; name: string };
  members?: { id: string; fullName: string; initials: string }[];
  attachments?: { id: string; name: string; url: string }[];
}

export interface TrelloComment {
  id: string;
  date: string;
  data: { text?: string };
  memberCreator?: { fullName?: string };
}

const TRELLO_LABEL_COLORS: Record<string, string> = {
  green: '#61bd4f', yellow: '#e6c60d', orange: '#ff9f1a', red: '#eb5a46',
  purple: '#c377e0', blue: '#0079bf', sky: '#00c2e0', lime: '#51e898',
  pink: '#ff78cb', black: '#344563',
};

export function labelColor(color: string | null): string {
  if (!color) return 'var(--text-faint)';
  // Trello also ships light/dark variants like "green_light"
  return TRELLO_LABEL_COLORS[color.split('_')[0]] ?? 'var(--text-faint)';
}

/** POST to the trello-board Netlify function with the caller's session token. */
export async function invokeTrelloBoard<T>(body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');
  const res = await fetch('/.netlify/functions/trello-board', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload.error || `Request failed (${res.status})`);
  return payload as T;
}
